/* eslint-disable import/no-cycle */
import { Cookies } from 'react-cookie'
import { decodeJwt, genericHandleApiError, requestOk } from '../../api/api-util'
import authServiceApi from '../../api/auth-service-api'
import userServiceApi from '../../api/user-service-api'
import { getText } from '../../internal/TextLabels'
import {
  GET_ACTIVE_USER_SUCCESS,
  LOGGED_OUT,
  LOGIN_ACTIVE,
  LOGIN_AUTHENTICATED,
  LOGIN_FAILED,
  LOGIN_RESET_STATUS,
  LOGIN_SUCCESS,
  LOGIN_WITH_TEMP_PASSWORD,
  MOCKING_USER,
  NOTIFY_DANGER,
  REFRESHED_LOGIN_TOKEN_SUCCESS,
  SET_STORE_SUCCESS,
} from './action-types'
import { genericError } from './action-utils'

import { Roles } from '../../models/roles'
import { NO_STORE } from '../../models/stores'
import { Authorities } from '../../models/authorities'
import { VALID_STORE_STATUSES } from '../../models/constants'

import {
  checkDebitDisabled,
  checkHasInvoiceNumber,
  getRepresentative,
  getStore,
} from './store-info-actions'
import { hasBonusCheckSupport } from './bonus-search-actions'

import { notify } from './notification-actions'
import { chatQuit } from './chat-actions'

//* ****************
// Local Helpers
//* ****************

export const isUserResursAdmin = state =>
  state.auth.role === Roles.RESURS_ADMIN ||
  (state.auth.originalUser &&
    state.auth.originalUser.role === Roles.RESURS_ADMIN)

const extractStoreInfo = (stores, storeId) => {
  for (let i = 0; i < stores.length; i += 1) {
    if (stores[i].id === storeId) {
      return {
        storeName: stores[i].tradeName,
        repId: stores[i].representativeId,
        accountSystemId: stores[i].accountSystemId,
      }
    }
  }
  return ''
}

const extractRepresentativeName = (representatives, repId) => {
  for (let i = 0; i < representatives.length; i += 1) {
    if (representatives[i].id === repId) {
      return representatives[i].name
    }
  }
  return ''
}

const hasAuthorities = (authorities, auth) => {
  let match = false
  for (let i = 0; i < authorities.length; i++) {
    if (auth === authorities[i]) {
      match = true
    }
  }
  return match
}

const setCookie = (auth, loginInfo) => {
  const cookies = new Cookies()
  if (loginInfo.store && loginInfo.country) {
    cookies.set(
      'store',
      {
        storeName: auth.store.storeName,
        storeNmbr: loginInfo.store,
        storeId: auth.store.storeUID,
      },
      { path: '/' },
    )
    cookies.set('country', { countryCode: loginInfo.country }, { path: '/' })
  }
}

//* ****************
// Actions
//* ****************

export const loginActive = () => ({ type: LOGIN_ACTIVE })

export const loginResetStatus = error => ({ error, type: LOGIN_RESET_STATUS })

export const loginError = error => ({ error, type: LOGIN_FAILED })

export const loginAuthenticated = response => ({
  response,
  type: LOGIN_AUTHENTICATED,
})

export const loginSuccess = (
  username,
  representativeName,
  storeName,
  storeId,
) => ({
  username,
  representativeName,
  storeName,
  storeId,
  type: LOGIN_SUCCESS,
})

export const refreshedToken = response => ({
  response,
  type: REFRESHED_LOGIN_TOKEN_SUCCESS,
})

export const getActiveUserSuccess = response => ({
  type: GET_ACTIVE_USER_SUCCESS,
  response,
})

export const tempPasswordActive = () => ({
  type: LOGIN_WITH_TEMP_PASSWORD,
})

export const setStoreSuccess = (response, representativeName, storeInfo) => ({
  response,
  representativeName,
  storeInfo,
  type: SET_STORE_SUCCESS,
})

export const logout = () => (dispatch, getState) => {
  const { decodedRefreshToken: { jti } = {} } = getState().auth || {}

  dispatch(chatQuit())
  dispatch({ type: LOGGED_OUT })

  return jti
    ? authServiceApi.logout(`token=${jti}`).catch(() => {})
    : Promise.resolve()
}

const callApiFunc = (apiFunc, apiReq, token) => {
  if (apiReq) {
    return apiFunc(apiReq, token)
  }
  return apiFunc(token)
}

/**
 * request
 *
 * Fetch request with JWT.
 * If http 401 try re-login with refreshToken
 */
export const request = (apiFunc, apiReq) => (dispatch, getState) =>
  callApiFunc(apiFunc, apiReq, getState().auth.token).catch(error => {
    if (error.status === 401) {
      return new Promise((resolve, reject) => {
        dispatch(authServiceApi.refreshLogin(getState().auth.refreshToken))
          .then(refreshResponse => {
            if (requestOk(refreshResponse)) {
              return refreshResponse.json().then(data => {
                dispatch(refreshedToken(decodeJwt(data)))
                return getState().auth.token
              })
            }
            dispatch(loginError(refreshResponse))
            return genericHandleApiError(refreshResponse)
          })
          .then(token =>
            callApiFunc(apiFunc, apiReq, token).then(resolve).catch(reject),
          )
          .catch(() => {
            dispatch(logout())
          })
      })
    }
    throw error
  })

const handleLoginFailed = error => dispatch => {
  dispatch(loginError(error))
  notify(
    NOTIFY_DANGER,
    error.desc ? error.desc : getText('login.genericLoginError'),
  )
}

const handleLoginFailedLegacy = (error, errorDesc) => dispatch => {
  dispatch(loginError(error))
  notify(NOTIFY_DANGER, errorDesc)
}

// Returns active user (user that's currently logged in)
export const getActiveUser = () => dispatch => {
  return dispatch(request(userServiceApi.getMe, null))
    .then(data => {
      dispatch(getActiveUserSuccess(data))
    })
    .catch(error => {
      genericError()
      throw error
    })
}

/**
 * setStore
 */
export const setStore = storeInfo => (dispatch, getState) => {
  const storeData = extractStoreInfo(
    getState().storeInfo.stores,
    storeInfo.store,
  )

  const repName = extractRepresentativeName(
    getState().storeInfo.representatives,
    storeData.repId,
  )

  return dispatch(
    request(authServiceApi.setStore, {
      representativeId: storeInfo.representative,
      storeId: storeInfo.store,
    }),
  )
    .then(data => {
      dispatch(getStore(storeInfo.store))
      dispatch(setStoreSuccess(decodeJwt(data), repName, storeData))
      dispatch(getRepresentative(storeData.repId))
      dispatch(checkHasInvoiceNumber())
      dispatch(checkDebitDisabled())
      dispatch(hasBonusCheckSupport())

      return Promise.resolve()
    })
    .catch(() => {
      notify(NOTIFY_DANGER, getText('store.select.error'))
    })
}

/**
 * Load user info
 * @param response
 */
export const loginLoadInfo = (response, loginInfo) => (dispatch, getState) => {
  dispatch(loginAuthenticated(response))

  let storeId = NO_STORE
  if (response.decodedToken.store) {
    storeId = response.decodedToken.store
  }

  const isResursAdmin = isUserResursAdmin(getState())
  const auths = getState().auth.authorities

  /* Login with temporarily password should be redirected to /password --> tempPasswordActive will do that */
  if (hasAuthorities(auths, Authorities.SET_PASSWORD)) {
    return dispatch(tempPasswordActive())
  }
  if (isResursAdmin && storeId === NO_STORE) {
    return dispatch(loginSuccess(loginInfo.username, '', '', ''))
  }
  if (
    !isResursAdmin &&
    storeId === NO_STORE &&
    hasAuthorities(auths, Authorities.SET_STORE)
  ) {
    return dispatch(getActiveUser())
      .then(() => {
        return dispatch(loginSuccess(loginInfo.username, '', '', ''))
      })
      .catch(error => {
        dispatch(handleLoginFailed(error))
        throw error
      })
  }
  return dispatch(getStore(storeId)).then(() => {
    const { status } = getState().storeInfo.currentStore
    if (!VALID_STORE_STATUSES.includes(status)) {
      const error = new Error()
      error.desc = getText('login.invalidStoreError')
      return dispatch(handleLoginFailed(error))
    }
    return dispatch(
      getRepresentative(getState().storeInfo.currentStore.representativeId),
    )
      .then(() => {
        if (
          !isResursAdmin &&
          !hasAuthorities(auths, Authorities.TEMPORARY_USER)
        ) {
          dispatch(getActiveUser()).then(() => {
            setCookie(getState().auth, loginInfo)
            dispatch(
              loginSuccess(
                loginInfo.username,
                getState().storeInfo.currentRepresentative.name,
                getState().storeInfo.currentStore.tradeName,
                getState().storeInfo.currentStore.accountSystemId,
              ),
            )
          })
        } else {
          setCookie(getState().auth, loginInfo)
          dispatch(
            loginSuccess(
              loginInfo.username,
              getState().storeInfo.currentRepresentative.name,
              getState().storeInfo.currentStore.tradeName,
              getState().storeInfo.currentStore.accountSystemId,
            ),
          )
        }
        dispatch(checkHasInvoiceNumber())
        dispatch(checkDebitDisabled())
        dispatch(hasBonusCheckSupport())
      })
      .catch(error => {
        dispatch(handleLoginFailedLegacy(error))
        throw error
      })
  })
}

export const login = userData => {
  return dispatch => {
    dispatch(loginActive())

    return authServiceApi
      .login(userData)
      .then(data => {
        dispatch(
          loginLoadInfo(data, {
            store: userData.store,
            country: userData.country,
            username: userData.username,
          }),
        )
      })
      .catch(error => {
        dispatch(handleLoginFailed(error))
      })
  }
}

export const loginWithAuthoritiesAndRole = userData => (dispatch, getState) => {
  const { currentStore: { accountSystemStoreId, country } = {} } =
    getState().storeInfo || {}
  return dispatch(request(authServiceApi.getSpecificToken, userData))
    .then(data => {
      dispatch({ type: MOCKING_USER, data: getState().auth })
      return data
    })
    .then(data => {
      dispatch(
        loginLoadInfo(data, {
          store: accountSystemStoreId,
          country,
          username: getState().auth.username,
        }),
      )
    })
    .catch(error => genericError(error))
}
